import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    Grid,
    Paper,
    Typography,
    TextField,
    IconButton,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
    Tooltip,
    InputAdornment,
    Chip,
    Avatar,
    useTheme,
    alpha,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Warning as WarningIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    Inventory as InventoryIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import InventoryAdjustment from './InventoryAdjustment';
import { formatGridCurrency } from '../../utils/currency';

const BranchInventory = ({ branchId, onInventoryUpdate, newProductId, showAddInventory, cachedProducts, cachedCategories, onRefreshSharedData }) => {
    const theme = useTheme();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [products, setProducts] = useState([]);
    const [branchProducts, setBranchProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [categories, setCategories] = useState([]);
    const [stockStatusFilter, setStockStatusFilter] = useState('');
    const [inactiveProducts, setInactiveProducts] = useState([]);
    
    // No more debouncing needed - using client-side filtering
    // Track if search is being filtered client-side  
    const isSearchPending = false; // Always false since filtering is instant
    const [editDialog, setEditDialog] = useState({ open: false, product: null });
    const [addInventoryDialog, setAddInventoryDialog] = useState({ open: false, selectedProductId: '' });
    const [restockDialog, setRestockDialog] = useState({ open: false, product: null });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, product: null });
    const [adjustmentDialog, setAdjustmentDialog] = useState({ open: false, product: null });

    // Get current branch ID
    const currentBranchId = branchId || user?.assignedBranches?.[0]?.id;

    const fetchData = useCallback(async () => {
        if (!currentBranchId) return;

        setLoading(true);
        try {
            // Always fetch fresh data - single API call for branch inventory
            // Use cached products and categories if available to reduce API calls
            const apiCalls = [api.get(`/branches/${currentBranchId}/inventory`)];

            if (cachedProducts && cachedCategories) {
                // Use cached data
                const [branchInventoryRes] = await Promise.all(apiCalls);
                setProducts(cachedProducts);
                setCategories(cachedCategories);
                setBranchProducts(branchInventoryRes.data.inventory || []);
            } else {
                // Fetch all data
                apiCalls.push(api.get('/products'), api.get('/products/categories'));
                const [branchInventoryRes, productsRes, categoriesRes] = await Promise.all(apiCalls);

                setProducts(productsRes?.data.products || []);
                setCategories(categoriesRes?.data.categories || []);
                setBranchProducts(branchInventoryRes.data.inventory || []);
            }

            setError('');
        } catch (error) {
            setError('Failed to load inventory data. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [currentBranchId, cachedProducts, cachedCategories]);

    useEffect(() => {
        if (currentBranchId) {
            fetchData();
        }
    }, [currentBranchId, fetchData]);

    // Handle navigation from product creation - wait until products are loaded
    useEffect(() => {
        if (showAddInventory && currentBranchId && !loading && products.length > 0) {
            // Check if the new product is already in inventory
            const isAlreadyInInventory = branchProducts.some(bp => bp.product_id === newProductId);
            if (isAlreadyInInventory) {
                // Product already in inventory, show success message instead
                setSuccessMessage('Product is already in inventory for this branch!');
                setTimeout(() => setSuccessMessage(''), 4000);
            } else {
                // Set the selected product ID when opening from navigation
                setAddInventoryDialog({
                    open: true,
                    selectedProductId: newProductId ? String(newProductId) : ''
                });
            }
        }
    }, [showAddInventory, currentBranchId, loading, products.length, newProductId, branchProducts]);

    const handleEditInventory = (product) => {
        // product already contains the inventory data from mergedProducts
        setEditDialog({ 
            open: true, 
            product: {
                ...product,
                currentQuantity: product.quantity || 0,
                currentReorderLevel: product.reorder_level || 0,
                currentUnitCost: product.unit_cost || 0,
                currentSellingPrice: product.selling_price || 0
            }
        });
    };

    const handleRestock = (product) => {
        setRestockDialog({ 
            open: true, 
            product: {
                ...product,
                currentQuantity: parseFloat(product.quantity) || 0
            }
        });
    };

    const handleAddInventory = async () => {
        // Fetch inactive products that can be reactivated
        try {
            const inactiveResponse = await api.get(`/branches/${currentBranchId}/inventory?includeInactive=true`);
            const allBranchProducts = inactiveResponse.data.inventory || [];
            const inactive = allBranchProducts.filter(item => item.status === 'inactive');
            setInactiveProducts(inactive);
        } catch (error) {
            setInactiveProducts([]);
        }
        setAddInventoryDialog({ open: true, selectedProductId: '' });
    };


    const handleAdjustStock = (product) => {
        setAdjustmentDialog({ 
            open: true, 
            product: {
                ...product,
                product_id: product.product_id || product.id
            }
        });
    };

    const handleAdjustmentComplete = async () => {
        await fetchData();
        if (onInventoryUpdate) onInventoryUpdate();
        setAdjustmentDialog({ open: false, product: null });
        setSuccessMessage('Stock adjustment completed successfully!');
        setTimeout(() => setSuccessMessage(''), 4000);
    };

    const handleSaveInventory = async (productId, inventoryData) => {
        try {
            // Use PUT for updating existing inventory
            await api.put(`/branches/${currentBranchId}/inventory/${productId}`, inventoryData);
            await fetchData();
            if (onInventoryUpdate) onInventoryUpdate();
            setEditDialog({ open: false, product: null });
            setSuccessMessage('Inventory updated successfully!');
            setError('');
            // Clear success message after 3 seconds
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (error) {
            setError('Failed to update inventory. Please try again.');
        }
    };

    const handleAddNewInventory = async (inventoryData) => {
        try {
            const response = await api.post(`/branches/${currentBranchId}/inventory`, inventoryData);
            await fetchData();
            if (onInventoryUpdate) onInventoryUpdate();
            setAddInventoryDialog({ open: false, selectedProductId: '' });

            // Show different message based on whether it was a reactivation or new addition
            const message = response.data.isReactivation
                ? `Product reactivated! ${response.data.message}`
                : response.data.message || 'Inventory successfully added!';

            setSuccessMessage(message);
            setError('');
            // Clear success message after 5 seconds
            setTimeout(() => setSuccessMessage(''), 5000);
        } catch (error) {
            setError('Failed to add inventory. Please try again.');
            setSuccessMessage('');
        }
    };

    const handleDeleteConfirm = (product) => {
        setDeleteDialog({ open: true, product });
    };

    const handleDeleteInventory = async () => {
        const { product } = deleteDialog;
        if (!product) return;
        
        try {
            const response = await api.delete(`/branches/${currentBranchId}/inventory/${product.product_id}`);
            await fetchData();
            if (onInventoryUpdate) onInventoryUpdate();
            setSuccessMessage(response.data.message || `${product.name} removed from inventory successfully!`);
            setError('');
            setDeleteDialog({ open: false, product: null });
            // Clear success message after 4 seconds
            setTimeout(() => setSuccessMessage(''), 4000);
        } catch (error) {
            setError('Failed to delete inventory. Please try again.');
            setDeleteDialog({ open: false, product: null });
        }
    };


    // Create a merged view of products with their inventory data
    // branchProducts only contains products that are stocked in this branch
    const baseProducts = branchProducts.map(branchProduct => ({
        ...branchProduct,
        id: branchProduct.product_id, // Ensure id is set for DataGrid
        hasInventory: true // All items shown are stocked in this branch
    }));

    // Smart client-side filtering (search + category + stock status)
    const mergedProducts = baseProducts.filter(product => {
        // Search filter
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            const matchesSearch = 
                product.name?.toLowerCase().includes(searchLower) ||
                product.sku?.toLowerCase().includes(searchLower) ||
                product.description?.toLowerCase().includes(searchLower) ||
                product.category_name?.toLowerCase().includes(searchLower);
            
            if (!matchesSearch) return false;
        }

        // Category filter
        if (selectedCategory && product.category_id?.toString() !== selectedCategory) {
            return false;
        }

        // Stock status filter
        if (stockStatusFilter) {
            const quantity = parseFloat(product.quantity) || 0;
            const reorderLevel = parseFloat(product.reorder_level) || 0;

            switch (stockStatusFilter) {
                case 'in_stock':
                    return quantity > 0 && (reorderLevel === 0 || quantity >= reorderLevel);
                case 'low_stock':
                    return quantity > 0 && reorderLevel > 0 && quantity < reorderLevel;
                case 'out_of_stock':
                    return quantity === 0;
                default:
                    return true;
            }
        }

        return true;
    });

    const columns = [
        {
            field: 'name',
            headerName: 'Product Name',
            flex: 2,
            minWidth: 220,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.4 }}>
                    {params.value}
                </Typography>
            )
        },
        {
            field: 'category_name',
            headerName: 'Category',
            flex: 1,
            minWidth: 130,
            renderCell: (params) => params.value ? (
                <Chip
                    label={params.value}
                    size="small"
                    sx={{
                        height: 24,
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        bgcolor: alpha(theme.palette.info.main, 0.08),
                        color: theme.palette.info.dark,
                    }}
                />
            ) : (
                <Typography variant="caption" color="text.disabled">Uncategorized</Typography>
            )
        },
        {
            field: 'quantity',
            headerName: 'Stock',
            flex: 0.7,
            minWidth: 80,
            renderCell: (params) => {
                const quantity = parseFloat(params.row.quantity) || 0;
                const reorderLevel = parseFloat(params.row.reorder_level) || 0;
                const isBelowReorder = quantity < reorderLevel && reorderLevel > 0;

                return (
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: isBelowReorder ? 700 : 500,
                            color: isBelowReorder ? 'warning.main' : 'text.primary',
                        }}
                    >
                        {quantity}
                    </Typography>
                );
            }
        },
        { field: 'reorder_level', headerName: 'Reorder Lvl', flex: 0.6, minWidth: 90 },
        {
            field: 'expiry_date',
            headerName: 'Expiry',
            flex: 0.9,
            minWidth: 120,
            renderCell: (params) => {
                const expiryDate = params.value;
                if (!expiryDate) return (
                    <Typography variant="caption" color="text.disabled">No expiry</Typography>
                );

                const expiry = new Date(expiryDate);
                const today = new Date();
                const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));

                let chipBg = alpha(theme.palette.text.secondary, 0.08);
                let chipTextColor = 'text.secondary';
                let label = expiry.toLocaleDateString();

                if (daysUntilExpiry < 0) {
                    chipBg = alpha(theme.palette.error.main, 0.1);
                    chipTextColor = theme.palette.error.dark;
                    label = `Expired`;
                } else if (daysUntilExpiry <= 3) {
                    chipBg = alpha(theme.palette.error.main, 0.1);
                    chipTextColor = theme.palette.error.dark;
                } else if (daysUntilExpiry <= 7) {
                    chipBg = alpha(theme.palette.warning.main, 0.1);
                    chipTextColor = theme.palette.warning.dark;
                } else if (daysUntilExpiry <= 30) {
                    chipBg = alpha(theme.palette.success.main, 0.08);
                    chipTextColor = theme.palette.success.dark;
                }

                return (
                    <Tooltip title={daysUntilExpiry < 0 ? `Expired ${Math.abs(daysUntilExpiry)} days ago` : `${daysUntilExpiry} days until expiry`}>
                        <Chip
                            label={label}
                            size="small"
                            sx={{
                                height: 24,
                                fontSize: '0.7rem',
                                fontWeight: 500,
                                bgcolor: chipBg,
                                color: chipTextColor,
                            }}
                        />
                    </Tooltip>
                );
            }
        },
        {
            field: 'unit_cost',
            headerName: 'Unit Cost (KES)',
            flex: 0.9,
            minWidth: 130,
            renderCell: (params) => formatGridCurrency(params.value)
        },
        {
            field: 'selling_price',
            headerName: 'Price (KES)',
            flex: 0.9,
            minWidth: 120,
            renderCell: (params) => formatGridCurrency(params.value)
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            minWidth: 110,
            renderCell: (params) => {
                const quantity = parseFloat(params.row.quantity) || 0;
                const reorderLevel = parseFloat(params.row.reorder_level) || 0;

                if (quantity === 0) {
                    return (
                        <Tooltip title="Out of stock - this product is stocked in this branch but quantity is 0">
                            <Chip
                                label="Out of Stock"
                                size="small"
                                sx={{
                                    height: 26,
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    bgcolor: alpha(theme.palette.error.main, 0.1),
                                    color: theme.palette.error.dark,
                                }}
                            />
                        </Tooltip>
                    );
                }

                if (quantity < reorderLevel && reorderLevel > 0) {
                    return (
                        <Tooltip title="Stock below reorder level">
                            <Chip
                                icon={<WarningIcon sx={{ fontSize: '14px !important' }} />}
                                label="Low Stock"
                                size="small"
                                sx={{
                                    height: 26,
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                                    color: theme.palette.warning.dark,
                                    '& .MuiChip-icon': {
                                        color: theme.palette.warning.dark,
                                    },
                                }}
                            />
                        </Tooltip>
                    );
                }

                return (
                    <Chip
                        label="In Stock"
                        size="small"
                        sx={{
                            height: 26,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            bgcolor: alpha(theme.palette.success.main, 0.1),
                            color: theme.palette.success.dark,
                        }}
                    />
                );
            }
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1.2,
            minWidth: 190,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <Tooltip title="Edit Inventory">
                        <IconButton
                            onClick={() => handleEditInventory(params.row)}
                            size="small"
                            sx={{
                                p: 0.75,
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                color: theme.palette.primary.main,
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.16),
                                },
                            }}
                        >
                            <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Restock">
                        <IconButton
                            onClick={() => handleRestock(params.row)}
                            size="small"
                            sx={{
                                p: 0.75,
                                bgcolor: alpha(theme.palette.success.main, 0.08),
                                color: theme.palette.success.main,
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.success.main, 0.16),
                                },
                            }}
                        >
                            <AddIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Adjust Stock">
                        <IconButton
                            onClick={() => handleAdjustStock(params.row)}
                            size="small"
                            sx={{
                                p: 0.75,
                                bgcolor: alpha(theme.palette.warning.main, 0.08),
                                color: theme.palette.warning.main,
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.warning.main, 0.16),
                                },
                            }}
                        >
                            <WarningIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove from Inventory">
                        <IconButton
                            onClick={() => handleDeleteConfirm(params.row)}
                            size="small"
                            sx={{
                                p: 0.75,
                                bgcolor: alpha(theme.palette.error.main, 0.08),
                                color: theme.palette.error.main,
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.error.main, 0.16),
                                },
                            }}
                        >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        }
    ];

    return (
        <>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            
            {successMessage && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {successMessage}
                </Alert>
            )}

            <Grid container spacing={3}>
                <Grid size={12}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        {/* Section Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        color: 'primary.main',
                                    }}
                                >
                                    <InventoryIcon sx={{ fontSize: 20 }} />
                                </Avatar>
                                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                                    Branch Inventory Management
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleAddInventory}
                                sx={{
                                    ml: 2,
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    px: 3,
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`,
                                    '&:hover': {
                                        boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.35)}`,
                                    },
                                }}
                            >
                                Add Inventory
                            </Button>
                        </Box>

                        {/* Search & Filter Row */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    fullWidth
                                    label="Search Products"
                                    placeholder="Search by name, SKU, or category..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: isSearchPending && (
                                            <InputAdornment position="end">
                                                <CircularProgress size={16} />
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            bgcolor: alpha(theme.palette.background.default, 0.6),
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Filter by Category"
                                    value={selectedCategory}
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            bgcolor: alpha(theme.palette.background.default, 0.6),
                                        }
                                    }}
                                >
                                    <MenuItem value="">All Categories</MenuItem>
                                    {categories.map((category) => (
                                        <MenuItem key={category.id} value={category.id}>
                                            {category.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Filter by Stock Status"
                                    value={stockStatusFilter}
                                    onChange={(e) => setStockStatusFilter(e.target.value)}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            bgcolor: alpha(theme.palette.background.default, 0.6),
                                        }
                                    }}
                                >
                                    <MenuItem value="">All Status</MenuItem>
                                    <MenuItem value="in_stock">In Stock</MenuItem>
                                    <MenuItem value="low_stock">Low Stock</MenuItem>
                                    <MenuItem value="out_of_stock">Out of Stock</MenuItem>
                                </TextField>
                            </Grid>
                        </Grid>

                        {/* Data Grid */}
                        <Paper
                            elevation={0}
                            sx={{
                                width: '100%',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 0,
                                overflow: 'hidden',
                            }}
                        >
                            <DataGrid
                                rows={mergedProducts}
                                columns={columns}
                                initialState={{
                                    pagination: {
                                        paginationModel: { page: 0, pageSize: 10 }
                                    }
                                }}
                                pageSizeOptions={[5, 10, 25, 50]}
                                disableRowSelectionOnClick
                                loading={loading}
                                autoHeight
                                getRowHeight={() => 'auto'}
                                slots={{
                                    noRowsOverlay: () => (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', py: 6, gap: 1 }}>
                                            <InventoryIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                                            <Typography color="text.secondary" variant="body1">
                                                No products found
                                            </Typography>
                                            <Typography color="text.disabled" variant="body2">
                                                Try adjusting your search or filters
                                            </Typography>
                                        </Box>
                                    )
                                }}
                                sx={{
                                    border: 'none',
                                    borderRadius: 0,
                                    fontSize: '0.875rem',
                                    // Header styling
                                    '& .MuiDataGrid-columnHeaders': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.06),
                                        borderBottom: '2px solid',
                                        borderColor: alpha(theme.palette.primary.main, 0.15),
                                        borderRadius: '0 !important',
                                    },
                                    '& .MuiDataGrid-columnHeaders [role="row"]': {
                                        borderRadius: '0 !important',
                                    },
                                    '& .MuiDataGrid-main': {
                                        borderRadius: 0,
                                    },
                                    '& .MuiDataGrid-columnHeader': {
                                        borderRight: '1px solid',
                                        borderColor: alpha(theme.palette.divider, 0.4),
                                        '&:last-child': {
                                            borderRight: 'none',
                                        },
                                    },
                                    '& .MuiDataGrid-columnHeaderTitle': {
                                        fontWeight: 700,
                                        fontSize: '0.8rem',
                                        color: theme.palette.text.primary,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.03em',
                                        whiteSpace: 'normal',
                                        lineHeight: 1.3,
                                        overflow: 'visible',
                                    },
                                    // Cell styling with text wrapping
                                    '& .MuiDataGrid-cell': {
                                        borderRight: '1px solid',
                                        borderColor: alpha(theme.palette.divider, 0.3),
                                        display: 'flex',
                                        alignItems: 'center',
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word',
                                        lineHeight: 1.5,
                                        py: 1,
                                        '&:last-child': {
                                            borderRight: 'none',
                                        },
                                    },
                                    // Row styling
                                    '& .MuiDataGrid-row:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.06),
                                    },
                                    // Focus styling
                                    '& .MuiDataGrid-cell:focus': {
                                        outline: 'none',
                                    },
                                    '& .MuiDataGrid-cell:focus-within': {
                                        outline: 'none',
                                    },
                                    '& .MuiDataGrid-columnHeader:focus': {
                                        outline: 'none',
                                    },
                                    '& .MuiDataGrid-columnHeader:focus-within': {
                                        outline: 'none',
                                    },
                                    // Footer / Pagination styling
                                    '& .MuiDataGrid-footerContainer': {
                                        borderTop: '2px solid',
                                        borderColor: alpha(theme.palette.primary.main, 0.15),
                                        bgcolor: alpha(theme.palette.background.default, 0.5),
                                        minHeight: 52,
                                    },
                                    '& .MuiTablePagination-root': {
                                        color: theme.palette.text.secondary,
                                        fontSize: '0.85rem',
                                    },
                                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                        fontWeight: 500,
                                        fontSize: '0.8rem',
                                        color: theme.palette.text.secondary,
                                    },
                                    '& .MuiTablePagination-select': {
                                        fontWeight: 600,
                                    },
                                    '& .MuiTablePagination-actions button': {
                                        color: theme.palette.primary.main,
                                        '&.Mui-disabled': {
                                            color: theme.palette.action.disabled,
                                        },
                                    },
                                    // Scrollbar
                                    '& .MuiDataGrid-virtualScroller': {
                                        '&::-webkit-scrollbar': {
                                            height: 8,
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            borderRadius: 4,
                                            bgcolor: alpha(theme.palette.text.secondary, 0.2),
                                        },
                                    },
                                    // Row border
                                    '& .MuiDataGrid-row': {
                                        borderBottom: '1px solid',
                                        borderColor: alpha(theme.palette.divider, 0.5),
                                        '&:last-child': {
                                            borderBottom: 'none',
                                        },
                                    },
                                }}
                            />
                        </Paper>
                    </Box>
                </Grid>
            </Grid>

            {/* Edit Inventory Dialog */}
            <Dialog 
                open={editDialog.open} 
                onClose={() => setEditDialog({ open: false, product: null })}
                maxWidth="sm"
                fullWidth
            >
                <form onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    handleSaveInventory(editDialog.product.product_id || editDialog.product.id, {
                        quantity: parseFloat(formData.get('quantity')) || 0,
                        reorderLevel: parseFloat(formData.get('reorderLevel')) || 0,
                        unitCost: parseFloat(formData.get('unitCost')) || 0,
                        sellingPrice: parseFloat(formData.get('sellingPrice')) || 0,
                        expiryDate: formData.get('expiryDate') || null
                    });
                }}>
                    <DialogTitle>
                        Update Inventory - {editDialog.product?.name}
                    </DialogTitle>
                    <DialogContent>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid size={12}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Current Stock"
                                    defaultValue={editDialog.product?.currentQuantity || 0}
                                    inputProps={{ min: 0 }}
                                    name="quantity"
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Reorder Level"
                                    defaultValue={editDialog.product?.currentReorderLevel || 0}
                                    inputProps={{ min: 0 }}
                                    name="reorderLevel"
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Unit Cost"
                                    defaultValue={editDialog.product?.currentUnitCost || 0}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    name="unitCost"
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Selling Price"
                                    defaultValue={editDialog.product?.currentSellingPrice || 0}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    name="sellingPrice"
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Expiry Date (Optional)"
                                    defaultValue={editDialog.product?.expiry_date ? new Date(editDialog.product.expiry_date).toISOString().split('T')[0] : ''}
                                    name="expiryDate"
                                    InputLabelProps={{ shrink: true }}
                                    helperText="Leave empty for non-perishable items"
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setEditDialog({ open: false, product: null })}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="contained">
                            Save
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Add New Inventory Dialog */}
            <Dialog
                open={addInventoryDialog.open}
                onClose={() => setAddInventoryDialog({ open: false, selectedProductId: '' })}
                maxWidth="sm"
                fullWidth
            >
                <form onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    handleAddNewInventory({
                        productId: parseInt(formData.get('productId')),
                        quantity: parseFloat(formData.get('quantity')) || 0,
                        reorderLevel: parseFloat(formData.get('reorderLevel')) || 0,
                        unitCost: parseFloat(formData.get('unitCost')) || 0,
                        sellingPrice: parseFloat(formData.get('sellingPrice')) || 0,
                        expiryDate: formData.get('expiryDate') || null
                    });
                }}>
                    <DialogTitle>Add New Inventory Item</DialogTitle>
                    <DialogContent>
                        {addInventoryDialog.selectedProductId && newProductId && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                Setting up inventory for your newly created product!
                            </Alert>
                        )}
                        {/* Check if there are available products */}
                        {(() => {
                            const availableProducts = products.filter(product =>
                                !branchProducts.find(bp => bp.product_id === product.id)
                            );
                            const hasAvailableProducts = availableProducts.length > 0 || inactiveProducts.length > 0;

                            if (!hasAvailableProducts) {
                                return (
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        All products are already in this branch's inventory. Create a new product first or check the inactive products.
                                    </Alert>
                                );
                            }
                            return null;
                        })()}
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid size={12}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Select Product"
                                    name="productId"
                                    value={addInventoryDialog.selectedProductId}
                                    onChange={(e) => setAddInventoryDialog(prev => ({
                                        ...prev,
                                        selectedProductId: e.target.value
                                    }))}
                                    required
                                >
                                    {/* Available products (not in inventory) - use branchProducts for accurate filtering */}
                                    {products
                                        .filter(product => !branchProducts.find(bp => bp.product_id === product.id))
                                        .map((product) => (
                                            <MenuItem key={product.id} value={String(product.id)}>
                                                {product.name} ({product.sku})
                                                {product.id === newProductId && (
                                                    <span style={{ color: 'green', marginLeft: '8px' }}>(New Product)</span>
                                                )}
                                            </MenuItem>
                                        ))}

                                    {/* Inactive products separator */}
                                    {inactiveProducts.length > 0 && (
                                        <MenuItem disabled style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }} value="">
                                            ── Reactivatable Products ──
                                        </MenuItem>
                                    )}

                                    {/* Inactive products that can be reactivated */}
                                    {inactiveProducts.map((product) => (
                                        <MenuItem key={`inactive-${product.product_id}`} value={String(product.product_id)}>
                                            {product.name} ({product.sku})
                                            <span style={{ color: 'orange', marginLeft: '8px', fontSize: '0.8em' }}>
                                                (Reactivate)
                                            </span>
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Initial Stock"
                                    defaultValue={0}
                                    inputProps={{ min: 0 }}
                                    name="quantity"
                                    required
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Reorder Level"
                                    defaultValue={0}
                                    inputProps={{ min: 0 }}
                                    name="reorderLevel"
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Unit Cost"
                                    defaultValue={0}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    name="unitCost"
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Selling Price"
                                    defaultValue={0}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    name="sellingPrice"
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Expiry Date (Optional)"
                                    name="expiryDate"
                                    InputLabelProps={{ shrink: true }}
                                    helperText="Set expiry date for perishable products"
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setAddInventoryDialog({ open: false, selectedProductId: '' })}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="contained">
                            Add Inventory
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Restock Dialog */}
            <Dialog 
                open={restockDialog.open} 
                onClose={() => setRestockDialog({ open: false, product: null })}
                maxWidth="sm"
                fullWidth
            >
                <form onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const additionalQuantity = parseFloat(formData.get('additionalQuantity')) || 0;
                    const newUnitCost = parseFloat(formData.get('newUnitCost')) || 0;
                    
                    if (additionalQuantity > 0 && newUnitCost >= 0) {
                        const currentQuantity = parseFloat(restockDialog.product.currentQuantity) || 0;
                        const newQuantity = currentQuantity + additionalQuantity;
                        
                        handleSaveInventory(restockDialog.product.product_id || restockDialog.product.id, {
                            quantity: newQuantity,
                            reorderLevel: parseFloat(restockDialog.product.reorder_level) || 0,
                            unitCost: newUnitCost,
                            sellingPrice: parseFloat(restockDialog.product.selling_price) || 0
                        });
                        setRestockDialog({ open: false, product: null });
                    }
                }}>
                    <DialogTitle sx={{ pb: 1 }}>
                        📦 Restock Item
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
                                {restockDialog.product?.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                SKU: {restockDialog.product?.sku}
                            </Typography>
                        </Box>
                        
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Current Stock: <strong>{restockDialog.product?.currentQuantity || 0} units</strong><br/>
                            Current Unit Cost: <strong>KES {Number(restockDialog.product?.unit_cost || 0).toFixed(2)}</strong>
                        </Alert>

                        <Grid container spacing={2}>
                            <Grid size={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Quantity to Add"
                                    defaultValue=""
                                    inputProps={{ min: 1, step: 1 }}
                                    name="additionalQuantity"
                                    required
                                    helperText="How many units to add"
                                />
                            </Grid>
                            <Grid size={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Unit Cost (KES)"
                                    defaultValue={Number(restockDialog.product?.unit_cost || 0).toFixed(2)}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    name="newUnitCost"
                                    required
                                    helperText="Cost per new unit"
                                />
                            </Grid>
                            <Grid size={12}>
                                <Alert severity="warning" sx={{ mt: 1 }}>
                                    <Typography variant="body2">
                                        <strong>💡 POS Best Practice:</strong><br/>
                                        • Always verify quantities before confirming<br/>
                                        • Different unit costs will be averaged automatically<br/>
                                        • This creates an audit trail for inventory changes
                                    </Typography>
                                </Alert>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button 
                            onClick={() => setRestockDialog({ open: false, product: null })}
                            variant="outlined"
                        >
                            Cancel
                        </Button>
                        <Button 
                            type="submit" 
                            variant="contained" 
                            color="success"
                            startIcon={<AddIcon />}
                        >
                            Add Stock
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, product: null })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon color="warning" />
                    Confirm Deletion
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Are you sure you want to remove <strong>{deleteDialog.product?.name}</strong> from inventory?
                    </Typography>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This action will:
                        <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                            <li>Remove all stock quantities ({deleteDialog.product?.quantity || 0} units)</li>
                            <li>Delete pricing information</li>
                            <li>Create an audit trail entry</li>
                        </ul>
                        This action cannot be undone.
                    </Alert>
                    <Typography variant="body2" color="text.secondary">
                        The product will remain in your product catalog but will no longer be available in this branch's inventory.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setDeleteDialog({ open: false, product: null })}
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleDeleteInventory}
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                    >
                        Delete Inventory
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Inventory Adjustment Dialog */}
            <InventoryAdjustment
                open={adjustmentDialog.open}
                onClose={() => setAdjustmentDialog({ open: false, product: null })}
                product={adjustmentDialog.product}
                onAdjustmentComplete={handleAdjustmentComplete}
                branchId={currentBranchId}
            />

        </>
    );
};

export default BranchInventory; 